import { useState, useEffect, useContext } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { toast } from 'sonner';
import { ChainlitContext, threadOperationsEvents } from '@chainlit/react-client';

import { operationsState, operationModalState } from '@chainlit/react-client';
import { operationsService } from '@/services/operationsService';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const colorOptions = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#10b981' },
  { name: 'Yellow', value: '#f59e0b' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Gray', value: '#6b7280' }
];

export default function OperationModal() {
  const apiClient = useContext(ChainlitContext);
  const [modalState, setModalState] = useRecoilState(operationModalState);
  const setOperations = useSetRecoilState(operationsState);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#6b7280'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (modalState.isOpen && modalState.mode === 'edit' && modalState.operationId) {
      loadOperationData(modalState.operationId);
    } else if (modalState.isOpen && modalState.mode === 'create') {
      setFormData({
        name: '',
        description: '',
        color: '#6b7280'
      });
    }
  }, [modalState]);

  const loadOperationData = async (operationId: string) => {
    setIsLoading(true);
    try {
      const operation = await operationsService.getOperation(apiClient, operationId);
      if (operation) {
        setFormData({
          name: operation.name || '',
          description: operation.description || '',
          color: operation.color || '#6b7280'
        });
      } else {
        toast.error('Operation not found');
        handleClose();
      }
    } catch (error) {
      toast.error('Failed to load operation data');
      console.error('Load operation error:', error);
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setModalState({ isOpen: false, mode: 'create' });
    setFormData({ name: '', description: '', color: '#6b7280' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Operation name is required');
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (modalState.mode === 'create') {
        const newOperation = await operationsService.createOperation(apiClient, formData);
        setOperations(prev => [newOperation, ...prev]);
        threadOperationsEvents.notifyOperationsUpdated();
        toast.success('Operation created successfully');
      } else if (modalState.mode === 'edit' && modalState.operationId) {
        const updatedOperation = await operationsService.updateOperation(apiClient, modalState.operationId, {
          ...formData,
          id: modalState.operationId
        });
        setOperations(prev => prev.map(op => op.id === modalState.operationId ? updatedOperation : op));
        threadOperationsEvents.notifyOperationsUpdated();
        toast.success('Operation updated successfully');
      }
      
      handleClose();
    } catch (error) {
      toast.error('Failed to save operation');
      console.error('Operation save error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={modalState.isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {modalState.mode === 'create' ? 'Create New Operation' : 'Edit Operation'}
          </DialogTitle>
          <DialogDescription>
            {modalState.mode === 'create' 
              ? 'Create a new operation to organize your chat threads.'
              : 'Update the operation details.'
            }
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="operation-name">Operation Name</Label>
            <Input
              id="operation-name"
              placeholder="e.g., Red Team Exercise 2025"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              disabled={isSubmitting || isLoading}
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="operation-description">Description</Label>
            <Textarea
              id="operation-description"
              placeholder="Brief description of the operation..."
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              disabled={isSubmitting || isLoading}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex flex-wrap gap-2">
              {colorOptions.map((color) => (
                <button
                  key={color.value}
                  type="button"
                  className={`
                    flex w-8 h-8 rounded-full border-2 transition-all
                    ${formData.color === color.value ? 'border-foreground scale-110' : 'border-border'}
                  `}
                  style={{ backgroundColor: color.value }}
                  onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                  disabled={isSubmitting || isLoading}
                  title={color.name}
                />
              ))}
            </div>
          </div>
        </form>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting || isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || isLoading || !formData.name.trim()}>
            {isLoading ? 'Loading...' : isSubmitting ? (modalState.mode === 'create' ? 'Creating...' : 'Updating...') : modalState.mode === 'create' ? 'Create Operation' : 'Update Operation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

